import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabaseClient';
import axios from 'axios';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

const UsageDashboard = () => {
  const [totalWords, setTotalWords] = useState(0);
  const [periodWords, setPeriodWords] = useState(0);
  const [periodLimitWords, setPeriodLimitWords] = useState(0);
  const [periodLabel, setPeriodLabel] = useState('day');
  const [tier, setTier] = useState('free');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgradeMessage, setUpgradeMessage] = useState(null);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Function to get or create session ID
  const getSessionId = () => {
    const newKey = 'lexory_session_id';
    const legacyKey = ['t', 'r', 'a', 'd', 'u', 'x'].join('') + '_session_id';
    let sessionId = localStorage.getItem(newKey) || localStorage.getItem(legacyKey);
    if (!sessionId) {
      sessionId = uuidv4();
    }
    localStorage.setItem(newKey, sessionId);
    return sessionId;
  };
  
  // Get plan from URL (monthly, quarterly, yearly)
  const getSelectedPlan = () => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get('plan');
    if (['monthly', 'quarterly', 'yearly'].includes(plan)) return plan;
    return 'monthly';
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgrade = params.get('upgrade');
    if (upgrade === 'success') {
      setUpgradeMessage({ severity: 'success', text: 'Payment successful. Premium will activate shortly.' });
      // Google Ads purchase conversion tracking
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18321092210/purchase',
        });
      }
    } else if (upgrade === 'cancel') {
      setUpgradeMessage({ severity: 'info', text: 'Upgrade canceled.' });
    }

    // Auto-trigger checkout if plan is specified and user is logged in
    const plan = params.get('plan');
    if (plan && ['monthly', 'quarterly', 'yearly'].includes(plan) && !upgrade) {
      // Will trigger checkout after auth check
      setTimeout(() => {
        const btn = document.getElementById('upgrade-btn');
        if (btn) btn.click();
      }, 500);
    }

    const fetchUsageStats = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const sessionId = getSessionId();

        const { data, error: sessionError } = await supabase.auth.getSession();
        const token = sessionError ? null : data?.session?.access_token;
        setIsAuthenticated(Boolean(token));

        const headers = {
          'X-Session-Id': sessionId
        };
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const response = await axios.get('/api/usage', { headers });
        const api = response?.data;

        setTier(api?.tier || 'free');
        setPeriodLabel(api?.period || 'day');
        setPeriodWords(api?.usedWords || 0);
        setPeriodLimitWords(api?.limitWords || 0);

        setTotalWords(api?.usedWords || 0);
      } catch (err) {
        const status = err?.response?.status;
        const apiError = err?.response?.data?.error;
        const apiDetails = err?.response?.data?.details;
        console.error('Unexpected error:', {
          status,
          apiError,
          apiDetails,
          message: err?.message,
        });
        setError(apiDetails || apiError || 'An unexpected error occurred. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUsageStats();
    
    // Refresh stats every minute
    const interval = setInterval(fetchUsageStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    setError(null);

    try {
      const sessionId = getSessionId();
      const { data, error: sessionError } = await supabase.auth.getSession();
      const token = sessionError ? null : data?.session?.access_token;

      if (!token) {
        setError('Please log in to upgrade to Premium.');
        return;
      }

      const headers = {
        'X-Session-Id': sessionId,
        Authorization: `Bearer ${token}`
      };

      const plan = getSelectedPlan();
      const response = await axios.post('/api/billing/create-checkout-session', { plan }, { headers });
      const url = response?.data?.url;

      if (!url) {
        setError('Failed to start checkout.');
        return;
      }

      window.location.assign(url);
    } catch (e) {
      const status = e?.response?.status;
      const apiError = e?.response?.data?.error;
      const apiDetails = e?.response?.data?.details;
      console.error('Checkout error:', {
        status,
        apiError,
        apiDetails,
        message: e?.message,
      });
      setError(apiDetails || apiError || 'Failed to start checkout. Please try again.');
    } finally {
      setUpgradeLoading(false);
    }
  };
  
  const usagePercentage = periodLimitWords ? Math.min(100, (periodWords / periodLimitWords) * 100) : 0;
  const isPremium = tier === 'premium';
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Your Translation Usage
      </Typography>

      {upgradeMessage && (
        <Alert severity={upgradeMessage.severity} sx={{ mb: 2 }}>
          {upgradeMessage.text}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={1}>
            <Typography variant="h6">Pricing & Plans</Typography>
            <Typography variant="body2" color="text.secondary">
              Upgrade anytime. Premium unlocks unlimited words and faster usage.
            </Typography>
          </Stack>
        </CardContent>
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h6">Free</Typography>
                    {!isPremium && <Chip label="Current plan" size="small" />}
                  </Stack>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    $0
                  </Typography>
                  <List dense disablePadding>
                    <ListItem disableGutters>
                      <ListItemText
                        primary="50 words per day"
                        secondary="Daily translation budget (resets each day)."
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemText
                        primary="Up to 15 words per request"
                        secondary="Max size of a single translation action (e.g., one selection)."
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: isPremium ? 'primary.main' : undefined,
                }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h6">Premium</Typography>
                    {isPremium && <Chip label="Current plan" size="small" color="primary" />}
                  </Stack>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    $8.99
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}/ month
                    </Typography>
                  </Typography>
                  <List dense disablePadding>
                    <ListItem disableGutters>
                      <ListItemText
                        primary="Unlimited words"
                        secondary="Translate as you read, without daily caps."
                      />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemText primary="Ideal for students, researchers, and language learners" />
                    </ListItem>
                  </List>
                </CardContent>

                <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
                  {!isPremium ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%' }}>
                      <Button
                        id="upgrade-btn"
                        variant="contained"
                        color="primary"
                        onClick={handleUpgrade}
                        disabled={!isAuthenticated || upgradeLoading}
                        fullWidth
                      >
                        {upgradeLoading ? 'Redirecting…' : 'Upgrade to Premium'}
                      </Button>
                      {!isAuthenticated && (
                        <Button variant="outlined" href="/login" fullWidth>
                          Log in
                        </Button>
                      )}
                    </Stack>
                  ) : (
                    <Button variant="contained" color="primary" disabled fullWidth>
                      You’re on Premium
                    </Button>
                  )}
                </CardActions>
              </Card>

              {!isAuthenticated && !isPremium && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Log in to upgrade and unlock Premium limits.
                </Typography>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Current Usage
              </Typography>
              {isPremium || !periodLimitWords ? (
                <>
                  <Typography variant="h4">
                    {periodWords}
                  </Typography>
                  <Typography color="text.secondary">
                    words this month (Premium)
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="h4">
                    {periodWords} / {periodLimitWords}
                  </Typography>
                  <Typography color="text.secondary">
                    words ({usagePercentage.toFixed(1)}% of {tier} tier, per {periodLabel})
                  </Typography>
                  {usagePercentage > 80 && (
                    <Typography color="error" sx={{ mt: 1 }}>
                      You're approaching your free tier limit!
                    </Typography>
                  )}
                  {usagePercentage >= 100 && (
                    <Typography color="error" sx={{ mt: 1 }}>
                      You've reached your current usage limit. Upgrade to Premium for higher limits.
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Usage
              </Typography>
              <Typography variant="h4">
                {totalWords}
              </Typography>
              <Typography color="text.secondary">
                words translated
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UsageDashboard;
